import * as React from "react"
import type { DateRange } from "react-day-picker"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { chartConfig } from "@/config/charts"
import { cn } from "@/lib/utils"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

// import { DateRangePicker } from "@/components/date-range-picker"

interface AreaChartProps {
  data: any
  toggledKeys?: string[]
  className?: string
}

export function AreaChartComponent({
  data,
  toggledKeys = [],
  className,
}: AreaChartProps) {
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    to: new Date(),
  })

  const [activeSeries, setActiveSeries] = React.useState<Array<string>>([])

  const handleLegendClick = (dataKey: string) => {
    setActiveSeries((prev) =>
      prev.includes(dataKey)
        ? prev.filter((el) => el !== dataKey)
        : [...prev, dataKey],
    )
  }

  React.useEffect(() => {
    setActiveSeries((prev) => {
      const newKeys = toggledKeys.filter((key) => !prev.includes(key))

      if (newKeys.length > 0) {
        return [...prev, ...newKeys]
      }

      return prev
    })
  }, [toggledKeys])

  const seriesKeys =
    data && data.length > 0
      ? Object.keys(data[0]).filter((key) => key !== "time")
      : []

  return (
    <div>
      {!data || data.length === 0 ? (
        <div
          className={cn(
            "flex h-[345px] w-full items-center justify-center",
            className,
          )}
        >
          <span className="text-muted-foreground">Loading...</span>
        </div>
      ) : (
        <ChartContainer
          config={chartConfig}
          className={cn("aspect-auto h-[345px] w-full", className)}
        >
          <AreaChart accessibilityLayer data={data}>
            {/*  <ChartLegend
              onClick={(entry: any) => handleLegendClick(entry.dataKey)}
              content={<ChartLegendContent />}
              layout="horizontal"
              align="right"
              verticalAlign="top"
              wrapperStyle={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            /> */}
            <ChartLegend
              onClick={(entry: any) => handleLegendClick(entry.dataKey)}
              content={<ChartLegendContent />}
              layout="horizontal"
              align="center"
              verticalAlign="bottom"
              wrapperStyle={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                marginTop: "10px",
              }}
            />

            <YAxis axisLine={false} tickLine={false} />
            <XAxis
              padding={{ left: 40 }}
              dataKey="time"
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value)

                const options: Intl.DateTimeFormatOptions =
                  className === "consumption-trends"
                    ? { month: "short", day: "numeric", year: "numeric" }
                    : { month: "short", day: "numeric" }

                return date.toLocaleDateString("en-US", options)
              }}
            />
            <CartesianGrid vertical={false} horizontal={false} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => {
                    const options: Intl.DateTimeFormatOptions =
                      className === "consumption-trends"
                        ? { month: "short", day: "numeric", year: "numeric" }
                        : { month: "short", day: "numeric" }

                    return new Date(value).toLocaleDateString("en-US", options)
                  }}
                  indicator="dot"
                />
              }
            />

            <defs>
              {seriesKeys.map((key) => {
                // Remove spaces from the key
                const sanitizedKey = key.trim().replace(/\s+/g, "") // Remove spaces
                const color =
                  chartConfig[sanitizedKey as keyof typeof chartConfig]?.color

                return (
                  <linearGradient
                    id={`color-${key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                    key={key}
                  >
                    <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                    <stop offset="50%" stopColor={color} stopOpacity={0.4} />
                    <stop offset="75%" stopColor={color} stopOpacity={0.1} />
                  </linearGradient>
                )
              })}
            </defs>
            {seriesKeys.map((key) => (
              <Area
                key={key}
                hide={activeSeries.includes(key)}
                type="linear"
                dataKey={key}
                fillOpacity={0.4}
                stroke={chartConfig[key as keyof typeof chartConfig].color}
                fill={`url(#color-${key})`}
                stackId={chartConfig[key as keyof typeof chartConfig].label}
              />
            ))}
          </AreaChart>
        </ChartContainer>
      )}
    </div>
  )
}
